import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input } from '../SubscriptionAppInput';
import { useLoyaltyAudit } from '../../contexts/LoyaltyAuditContext';
import { formatShortDate } from '../../utils/dateFormat';
import type { CellarDoorVisit } from '../../data/mockContactVisits';
import { X, CheckCircle, Circle, Download, ChevronDown, HelpCircle, MoreHorizontal, Lock, Moon, Gift, RotateCcw, Pause, AlertTriangle, Info, ArrowRight, Percent, Truck, Ticket, Copy, Check } from 'lucide-react';
// Rewards this member has been issued, newest first. The code is what support
// needs when a member loses their email.
type IssuedRewardStatus = 'Active' | 'Used' | 'Expired';
interface IssuedReward {
  id: string;
  name: string;
  typeLabel: 'Money off' | 'Free shipping' | 'Voucher';
  code: string | null;
  issued: string;
  expires: string | null;
  status: IssuedRewardStatus;
  usedOnOrder?: string;
}
const ISSUED_REWARDS: IssuedReward[] = [{
  id: 'ir-1',
  name: '$10 Off Next Order',
  typeLabel: 'Money off',
  code: 'WP-8KD2-M4QX',
  issued: '2 Sep 2026',
  expires: '2 Mar 2027',
  status: 'Active'
}, {
  id: 'ir-2',
  name: 'Complimentary tasting experience',
  typeLabel: 'Voucher',
  code: 'WP-V7TN-3RLA',
  issued: '14 Aug 2026',
  expires: '14 Feb 2027',
  status: 'Active'
}, {
  id: 'ir-3',
  name: 'Free Shipping',
  typeLabel: 'Free shipping',
  code: null,
  issued: '20 Jul 2026',
  expires: null,
  status: 'Used',
  usedOnOrder: '#4488'
}, {
  id: 'ir-4',
  name: '10% Off Next Order',
  typeLabel: 'Money off',
  code: 'WP-2HQE-9CZP',
  issued: '3 Feb 2026',
  expires: '3 Aug 2026',
  status: 'Expired'
}, {
  id: 'ir-5',
  name: 'Complimentary tasting experience',
  typeLabel: 'Voucher',
  code: 'WP-Q4MW-8YTB',
  issued: '14 Aug 2025',
  expires: '14 Feb 2026',
  status: 'Expired'
}];
const issuedRewardIcon: Record<IssuedReward['typeLabel'], React.FC<{
  className?: string;
}>> = {
  'Money off': Percent,
  'Free shipping': Truck,
  Voucher: Ticket
};
const issuedRewardStatusStyle: Record<IssuedRewardStatus, string> = {
  Active: 'bg-[#D1FAE5] text-[#047857]',
  Used: 'bg-[#E2E8F0] text-[#475569]',
  Expired: 'bg-[#FEE2E2] text-[#B91C1C]'
};
// Tier calculation basis (mirrors the Loyalty Rules page, which defaults to
// "Points"). Drives whether tier-progress reads in points or dollars.
const TIER_CALCULATION_BASIS: 'Points' | 'Annual Spend' | 'Lifetime Spend' = 'Points';
// Loyalty tier model (mirrors Loyalty Members page) — used by single-member
// Adjust Points and Override Tier modals on this page.
const CD_TIERS = [{
  name: 'Enthusiast',
  min: 0,
  max: 499
}, {
  name: 'Connoisseur',
  min: 500,
  max: 1999
}, {
  name: 'Sommelier',
  min: 2000,
  max: 4999
}, {
  name: 'Vintner',
  min: 5000,
  max: Infinity
}];
const cdGetTierForPoints = (points: number): string => {
  for (const t of CD_TIERS) {
    if (points >= t.min && points <= t.max) return t.name;
  }
  return 'Enthusiast';
};
const cdGetTierRank = (name: string): number => {
  const i = CD_TIERS.findIndex(t => t.name === name);
  return i >= 0 ? i : 0;
};
const CD_ADD_REASONS = ['Cellar door visit', 'Wine club event', 'Tasting experience', 'Program migration', 'Correction', 'Goodwill gesture', 'Promotional campaign', 'Other'];
const CD_REMOVE_REASONS = ['Correction', 'Program migration', 'Points expiry', 'Refund adjustment', 'Order cancellation', 'Other'];
const CD_TIER_REASONS = ['Event bonus', 'Program migration', 'Correction', 'Goodwill gesture', 'Promotional campaign', 'Other'];
type WalletPassState = 'NotIssued' | 'Sent' | 'Active' | 'SyncIssue' | 'Revoked';
interface ContactLoyaltyTabProps {
  contactId: string;
  contactName: string;
  tierLocked?: boolean;
  tierLockedBy?: string;
  tierLockedAt?: string;
  walletPassState?: WalletPassState;
  walletPassLastSyncedAt?: string;
  walletPassSentAt?: string;
  walletPassRevokedAt?: string | null;
  // Cellar door visits (newest first); full history lives on the Overview tab
  visits?: CellarDoorVisit[];
  onViewVisits?: () => void;
}
export function ContactLoyaltyTab({
  contactId,
  contactName,
  tierLocked = true,
  tierLockedBy = 'Sarah Chen',
  tierLockedAt = 'Feb 10, 2026',
  walletPassState = 'Active',
  walletPassLastSyncedAt = 'Feb 8, 2026',
  walletPassSentAt = 'Mar 15, 2025',
  walletPassRevokedAt = null,
  visits = [],
  onViewVisits
}: ContactLoyaltyTabProps) {
  const navigate = useNavigate();
  const {
    addAuditEvent
  } = useLoyaltyAudit();
  const [isLoyaltyMoreOpen, setIsLoyaltyMoreOpen] = useState(false);
  const [isUnlockTierModalOpen, setIsUnlockTierModalOpen] = useState(false);
  const [issuedFilter, setIssuedFilter] = useState<'All' | IssuedRewardStatus>('All');
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const visibleIssuedRewards = issuedFilter === 'All' ? ISSUED_REWARDS : ISSUED_REWARDS.filter(reward => reward.status === issuedFilter);
  const copyRewardCode = (code: string) => {
    navigator.clipboard?.writeText(code);
    setCopiedCode(code);
    window.setTimeout(() => setCopiedCode(null), 2000);
  };
  const [isReEnrolModalOpen, setIsReEnrolModalOpen] = useState(false);
  // Single-member loyalty action modals (4.1)
  const [isAdjustPointsModalOpen, setIsAdjustPointsModalOpen] = useState(false);
  const [isOverrideTierModalOpen, setIsOverrideTierModalOpen] = useState(false);
  // Adjust Points form state
  const [adjustmentType, setAdjustmentType] = useState<'add' | 'remove' | 'reset'>('add');
  const [adjustmentAmount, setAdjustmentAmount] = useState('');
  const [adjustReasonPreset, setAdjustReasonPreset] = useState('');
  const [adjustReasonCustom, setAdjustReasonCustom] = useState('');
  const [isAdjustReasonOpen, setIsAdjustReasonOpen] = useState(false);
  const [resetConfirmation, setResetConfirmation] = useState('');
  // Override Tier form state
  const [targetTier, setTargetTier] = useState('');
  const [isTargetTierOpen, setIsTargetTierOpen] = useState(false);
  const [lockTierOnOverride, setLockTierOnOverride] = useState(false);
  const [overrideReasonPreset, setOverrideReasonPreset] = useState('');
  const [overrideReasonCustom, setOverrideReasonCustom] = useState('');
  const [isOverrideReasonOpen, setIsOverrideReasonOpen] = useState(false);
  // Current loyalty snapshot for this member (matches the summary card)
  const currentPoints = 4250;
  const currentTier = 'Sommelier';
  const [walletPassStatePreview, setWalletPassStatePreview] = useState<WalletPassState>(walletPassState);
  const [enrolmentStatePreview, setEnrolmentStatePreview] = useState<'Active' | 'Dormant' | 'NotEnrolled' | 'OptedOut' | 'Paused'>('Active');
  const isDormant = enrolmentStatePreview === 'Dormant';
  const isNotEnrolled = enrolmentStatePreview === 'NotEnrolled';
  const isOptedOut = enrolmentStatePreview === 'OptedOut';
  const isPaused = enrolmentStatePreview === 'Paused';
  const isMuted = isOptedOut || isPaused;
  // Qualification window mirrors the Loyalty Rules page — fixed windows close on a
  // date, so progress carries a deadline and tier is held until period close.
  const [qualificationWindowPreview, setQualificationWindowPreview] = useState<'rolling' | 'calendar' | 'financial'>('rolling');
  // A flat points program has one tier, so there is nothing to climb toward.
  const [isSingleTierProgram, setIsSingleTierProgram] = useState(false);
  const isFixedWindow = TIER_CALCULATION_BASIS === 'Annual Spend' && qualificationWindowPreview !== 'rolling';
  const windowCloseLabel = qualificationWindowPreview === 'calendar' ? '31 December' : '30 June';
  const windowHeldUntil = qualificationWindowPreview === 'calendar' ? '31 December 2026' : '30 June 2027';
  // Emit a loyalty audit event into the global Operations Activity Log.
  // Single-member events resolve to this contact (entityType 'contact').
  const logLoyaltyAudit = (event: {
    eventType: string;
    activity: string;
    status?: string;
    changes?: {
      field: string;
      before: string;
      after: string;
    }[];
  }) => addAuditEvent({
    entity: contactName || null,
    entityType: 'contact',
    ...event
  });
  // Open + reset the single-member Adjust Points modal
  const openAdjustPoints = () => {
    setAdjustmentType('add');
    setAdjustmentAmount('');
    setAdjustReasonPreset('');
    setAdjustReasonCustom('');
    setIsAdjustReasonOpen(false);
    setResetConfirmation('');
    setIsAdjustPointsModalOpen(true);
  };
  // Open + reset the single-member Override Tier modal
  const openOverrideTier = () => {
    setTargetTier('');
    setIsTargetTierOpen(false);
    setLockTierOnOverride(false);
    setOverrideReasonPreset('');
    setOverrideReasonCustom('');
    setIsOverrideReasonOpen(false);
    setIsOverrideTierModalOpen(true);
  };
  // Derived previews for the Adjust Points modal
  const adjustAmountNum = parseInt(adjustmentAmount) || 0;
  const adjustNewBalance = adjustmentType === 'reset' ? 0 : adjustmentType === 'add' ? currentPoints + adjustAmountNum : Math.max(0, currentPoints - adjustAmountNum);
  const adjustNewTier = cdGetTierForPoints(adjustNewBalance);
  const adjustGoesBelowZero = adjustmentType === 'remove' && adjustAmountNum > currentPoints;
  const adjustReasonResolved = adjustReasonPreset === 'Other' ? adjustReasonCustom.trim() : adjustReasonPreset;
  const adjustApplyDisabled = !adjustReasonResolved || (adjustmentType === 'reset' ? resetConfirmation !== 'RESET' : adjustAmountNum <= 0);
  // Derived previews for the Override Tier modal
  const overrideIsDowngrade = !!targetTier && cdGetTierRank(targetTier) < cdGetTierRank(currentTier);
  const overrideReasonResolved = overrideReasonPreset === 'Other' ? overrideReasonCustom.trim() : overrideReasonPreset;
  const overrideApplyDisabled = !targetTier || !overrideReasonResolved;
  return <>
      <div className="space-y-6" data-id="element-1125" data-mp="3c3zrcv1nnejr">
        {/* Dev Preview Switcher — Enrolment state */}
        <div className="bg-[#FEF3C7] border border-[#FDE68A] rounded-md px-4 py-2.5 flex items-center gap-3 mb-2" data-id="element-1126" data-mp="3h17ze2t3a27a">
          <span className="text-[12px] font-medium text-[#92400E]" data-id="element-1127" data-mp="3j7ccpcppqvln">
            🛠 Dev preview — Enrolment state:
          </span>
          <div className="flex items-center gap-1.5" data-id="element-1128" data-mp="39ar3sgakp0cj">
            {([{
            key: 'Active',
            label: 'Active'
          }, {
            key: 'Dormant',
            label: 'Dormant'
          }, {
            key: 'NotEnrolled',
            label: 'Not Enrolled'
          }, {
            key: 'OptedOut',
            label: 'Opted Out'
          }, {
            key: 'Paused',
            label: 'Paused'
          }] as const).map(s => <button key={s.key} onClick={() => setEnrolmentStatePreview(s.key)} className={`h-7 px-2.5 text-[12px] font-medium rounded transition-colors ${enrolmentStatePreview === s.key ? 'bg-[#92400E] text-white' : 'bg-white text-[#92400E] border border-[#FDE68A] hover:bg-[#FEF3C7]'}`} data-id="element-1129" data-mp="3s8k9trghtqe5">
                {s.label}
              </button>)}
          </div>
        </div>
        {/* Dev Preview Switcher — Wallet pass state */}
        <div className="bg-[#FEF3C7] border border-[#FDE68A] rounded-md px-4 py-2.5 flex items-center gap-3 mb-4" data-id="element-1130" data-mp="2e85z165f02g4">
          <span className="text-[12px] font-medium text-[#92400E]" data-id="element-1131" data-mp="29932yb7ex6ka">
            🛠 Dev preview — Wallet pass state:
          </span>
          <div className="flex items-center gap-1.5" data-id="element-1132" data-mp="2j077egzgp90v">
            {(['NotIssued', 'Sent', 'Active', 'SyncIssue', 'Revoked'] as const).map(state => <button key={state} onClick={() => setWalletPassStatePreview(state)} className={`h-7 px-2.5 text-[12px] font-medium rounded transition-colors ${walletPassStatePreview === state ? 'bg-[#92400E] text-white' : 'bg-white text-[#92400E] border border-[#FDE68A] hover:bg-[#FEF3C7]'}`} data-id="element-1133" data-mp="1vbo8ut67salz">
                {state}
              </button>)}
          </div>
        </div>

        {/* Dev Preview Switcher — Tier structure */}
        <div className="bg-[#FEF3C7] border border-[#FDE68A] rounded-md px-4 py-2.5 flex items-center gap-3 mb-2" data-id="element-1134" data-mp="3ft2mgo5j04ji">
          <span className="text-[12px] font-medium text-[#92400E]" data-id="element-1135" data-mp="28eh19znlyuzc">
            🛠 Dev preview — Tier structure:
          </span>
          <div className="flex items-center gap-1.5" data-id="element-1136" data-mp="2dcsbibhtf7i3">
            {([{
            key: false,
            label: 'Multiple tiers'
          }, {
            key: true,
            label: 'Single tier'
          }] as const).map(t => <button key={t.label} onClick={() => setIsSingleTierProgram(t.key)} className={`h-7 px-2.5 text-[12px] font-medium rounded transition-colors ${isSingleTierProgram === t.key ? 'bg-[#92400E] text-white' : 'bg-white text-[#92400E] border border-[#FDE68A] hover:bg-[#FEF3C7]'}`} data-id="element-1137" data-mp="2mcyjjhuqvf9v">
                {t.label}
              </button>)}
          </div>
        </div>

        {/* Dev Preview Switcher — Qualification window */}
        <div className="bg-[#FEF3C7] border border-[#FDE68A] rounded-md px-4 py-2.5 flex items-center gap-3 mb-4" data-id="element-1138" data-mp="23hi75h3y2o15">
          <span className="text-[12px] font-medium text-[#92400E]" data-id="element-1139" data-mp="1az35kcfktck8">
            🛠 Dev preview — Qualification window:
          </span>
          <div className="flex items-center gap-1.5" data-id="element-1140" data-mp="13eitkmpd2c5n">
            {([{
            key: 'rolling',
            label: 'Rolling 12 months'
          }, {
            key: 'calendar',
            label: 'Calendar year'
          }, {
            key: 'financial',
            label: 'Financial year'
          }] as const).map(w => <button key={w.key} onClick={() => setQualificationWindowPreview(w.key)} className={`h-7 px-2.5 text-[12px] font-medium rounded transition-colors ${qualificationWindowPreview === w.key ? 'bg-[#92400E] text-white' : 'bg-white text-[#92400E] border border-[#FDE68A] hover:bg-[#FEF3C7]'}`} data-id="element-1141" data-mp="pfar9kxwinx8">
                {w.label}
              </button>)}
          </div>
        </div>
        {isNotEnrolled /* Not Enrolled empty state */ ? <div className="bg-white border border-[#E2E8F0] rounded-lg shadow-sm p-12 flex flex-col items-center text-center" data-id="element-1142" data-mp="6nhynrmqv4q1">
            <div className="w-12 h-12 rounded-full bg-[#F1F5F9] flex items-center justify-center mb-4" data-id="element-1143" data-mp="3swsvzjidnetf">
              <Gift className="w-6 h-6 text-[#94A3B8]" data-id="element-1144" data-mp="1wsuzr8fvcj3w" />
            </div>
            <h3 className="text-lg font-semibold text-[#0F172A] mb-1" data-id="element-1145" data-mp="1wvqz917d7fug">
              Not enrolled in loyalty
            </h3>
            <p className="text-sm text-[#64748B] max-w-md mb-6" data-id="element-1146" data-mp="1n43c3chuckys">
              This store enrols subscribers automatically. {contactName} isn't
              currently enrolled, but you can add them manually below.
            </p>
            <button onClick={() => {
          logLoyaltyAudit({
            eventType: 'Loyalty Status',
            activity: `Member enrolled — ${contactName} added manually`,
            changes: [{
              field: 'Enrolment State',
              before: 'Not enrolled',
              after: 'Active'
            }, {
              field: 'Welcome Email',
              before: '—',
              after: 'Sent'
            }, {
              field: 'Wallet Pass',
              before: '—',
              after: 'Issued'
            }]
          });
          setEnrolmentStatePreview('Active');
        }} className="h-9 px-4 bg-[#0EA5E9] text-white rounded-md text-sm font-medium hover:bg-[#0284C7] transition-colors" data-id="element-1147" data-mp="1e23q05ryyqoz">
              Enrol {contactName}
            </button>
          </div> : <>
            {isOptedOut /* Opted Out banner */ && <div className="bg-[#FEF2F2] border border-[#FECACA] rounded-lg px-5 py-4 flex items-start justify-between gap-4" data-id="element-1148" data-mp="wtkt79zkdxi5">
                <div className="flex items-start gap-3" data-id="element-1149" data-mp="1ak6boxl3w5fi">
                  <div className="w-8 h-8 rounded-full bg-[#FEE2E2] flex items-center justify-center flex-shrink-0" data-id="element-1150" data-mp="1amsl72rk4v0d">
                    <X className="w-4 h-4 text-[#DC2626]" data-id="element-1151" data-mp="1kivi9u7jy3gg" />
                  </div>
                  <div data-id="element-1152" data-mp="1kgst1ce84ecs">
                    <div className="text-sm font-semibold text-[#991B1B]" data-id="element-1153" data-mp="1fjizplnprijo">
                      Opted out of loyalty on 12 Jan 2026
                    </div>
                    <div className="text-sm text-[#B91C1C] mt-0.5" data-id="element-1154" data-mp="3mcmhur0afljj">
                      The member chose to leave. Their points and tier history
                      are retained but inactive.
                    </div>
                  </div>
                </div>
                <button onClick={() => setIsReEnrolModalOpen(true)} className="h-9 px-3 bg-white border border-[#CBD5E1] text-[#0F172A] rounded-md text-sm font-medium hover:bg-[#F8FAFC] transition-colors flex items-center gap-1.5 flex-shrink-0" data-id="element-1155" data-mp="ewjrz51fd7rt">
                  <RotateCcw className="w-4 h-4" data-id="element-1156" data-mp="orlju8n34ny3" />
                  Re-enrol
                </button>
              </div>}
            {isPaused /* Paused banner */ && <div className="bg-[#FFFBEB] border border-[#FDE68A] rounded-lg px-5 py-4 flex items-start justify-between gap-4" data-id="element-1157" data-mp="xtbj20n7kr6f">
                <div className="flex items-start gap-3" data-id="element-1158" data-mp="12nfq0jwh4z7i">
                  <div className="w-8 h-8 rounded-full bg-[#FEF3C7] flex items-center justify-center flex-shrink-0" data-id="element-1159" data-mp="17je8jqv4fn00">
                    <Pause className="w-4 h-4 text-[#92400E]" data-id="element-1160" data-mp="1cl3mbe9qaers" />
                  </div>
                  <div data-id="element-1161" data-mp="1hjewxr7uktb2">
                    <div className="text-sm font-semibold text-[#92400E]" data-id="element-1162" data-mp="1hivd24ymzuue">
                      Loyalty paused on 5 Feb 2026 by Sarah Chen
                    </div>
                    <div className="text-sm text-[#B45309] mt-0.5" data-id="element-1163" data-mp="2x0jkne8ashzj">
                      Earning and redemptions are on hold. The points balance is
                      frozen until loyalty is resumed.
                    </div>
                  </div>
                </div>
                <button onClick={() => {
            logLoyaltyAudit({
              eventType: 'Loyalty Status',
              activity: 'Loyalty resumed',
              changes: [{
                field: 'Enrolment State',
                before: 'Paused',
                after: 'Active'
              }]
            });
            setEnrolmentStatePreview('Active');
          }} className="h-9 px-3 bg-[#0EA5E9] text-white rounded-md text-sm font-medium hover:bg-[#0284C7] transition-colors flex items-center gap-1.5 flex-shrink-0" data-id="element-1164" data-mp="3kqvyt6u4zayk">
                  <RotateCcw className="w-4 h-4" data-id="element-1165" data-mp="2kr7clhpeq14" />
                  Resume
                </button>
              </div>}
            <div className={isMuted ? 'space-y-6 opacity-60 pointer-events-none select-none' : 'space-y-6'} data-id="element-1166" data-mp="3722dj9eb1lrd">
              {/* Row 1: Summary & Progress */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-1167" data-mp="2y3grh49mjolu">
                {/* Loyalty Summary */}
                <div className="bg-white border border-[#E2E8F0] rounded-lg shadow-sm" data-id="element-1168" data-mp="332tk65n6fpwc">
                  <div className="p-6 border-b border-[#E2E8F0] flex items-center justify-between" data-id="element-1169" data-mp="3qr30wt5j9ngm">
                    <h3 className="text-base font-semibold text-[#0F172A]" data-id="element-1170" data-mp="3urwrxve9vidq">
                      Loyalty Summary
                    </h3>
                    {isDormant && <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#F1F5F9] text-[#64748B] border border-[#E2E8F0]" data-id="element-1171" data-mp="3gvj8igq6h6wm">
                        <Moon className="w-3 h-3" data-id="element-1172" data-mp="25cf8st6q3axk" />
                        Dormant
                      </span>}
                  </div>
                  <div className="p-6" data-id="element-1173" data-mp="22tk6aeo30g4y">
                    <div className="mb-6" data-id="element-1174" data-mp="22siv8a72ftn8">
                      <div className="text-3xl font-bold text-[#0F172A]" data-id="element-1175" data-mp="22vesdwb3aeam">
                        4,250
                      </div>
                      <div className="text-sm text-[#64748B]" data-id="element-1176" data-mp="2bus46vmoreyi">points</div>
                      {isDormant && <p className="text-xs text-[#94A3B8] mt-2 leading-relaxed" data-id="element-1177" data-mp="26vf6ffua0nh5">
                          No loyalty activity in over 12 months. Points and tier
                          are retained but the member isn't currently earning.
                        </p>}
                      {isPaused && <p className="text-xs text-[#94A3B8] mt-2 leading-relaxed" data-id="element-1178" data-mp="1f26dk7m0qijg">
                          Balance frozen while loyalty is paused.
                        </p>}
                    </div>
                    <div className="space-y-3" data-id="element-1179" data-mp="10f3gg21lybwn">
                      <div className="flex justify-between items-center" data-id="element-1180" data-mp="10dsd23c2r662">
                        <span className="text-sm text-[#475569]" data-id="element-1181" data-mp="vgjtpzf6ww38">Tier</span>
                        <div className="flex items-center gap-1.5" data-id="element-1182" data-mp="3o182sx2az2c6">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#EDE9FE] text-[#6F42C1]" data-id="element-1183" data-mp="3o1hv4ratlll1">
                            Sommelier
                          </span>
                          {tierLocked && <button onClick={() => setIsUnlockTierModalOpen(true)} aria-label="Tier is manually locked. Click to unlock." className="relative group flex items-center justify-center w-5 h-5 rounded hover:bg-[#F1F5F9] transition-colors" data-id="element-1184" data-mp="3t0bayqdkxood">
                              <Lock className="w-3.5 h-3.5 text-[#64748B] cursor-help" data-id="element-1185" data-mp="6jyp5c663l8u" />
                              <div className="absolute top-full right-0 mt-2 px-3 py-2 bg-[#0F172A] text-white text-xs font-normal normal-case tracking-normal rounded-md shadow-lg whitespace-nowrap opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50" data-id="element-1186" data-mp="kjgcro7q8sgm">
                                Tier locked by {tierLockedBy} on {tierLockedAt}{' '}
                                — won't auto-calculate
                                <div className="absolute bottom-full right-2 w-0 h-0 border-l-4 border-r-4 border-b-4 border-l-transparent border-r-transparent border-b-[#0F172A]" data-id="element-1187" data-mp="wwtv2692799e" />
                              </div>
                            </button>}
                        </div>
                      </div>
                      <div className="flex justify-between items-start" data-id="element-1188" data-mp="s1o2wpp2cycv">
                        <span className="text-sm text-[#475569]" data-id="element-1189" data-mp="e2y3v4iooolv">
                          Wallet Pass
                        </span>
                        <div className="flex flex-col items-end gap-1" data-id="element-1190" data-mp="463af019peeg">
                          {walletPassStatePreview === 'NotIssued' && <>
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#F1F5F9] text-[#475569] border border-[#E2E8F0]" data-id="element-1191" data-mp="95y1b3l1m3zi">
                                Not issued
                              </span>
                              <button onClick={() => {
                          logLoyaltyAudit({
                            eventType: 'Loyalty Status',
                            activity: 'Wallet pass issued',
                            changes: [{
                              field: 'Wallet Pass',
                              before: 'Not issued',
                              after: 'Sent'
                            }]
                          });
                          setWalletPassStatePreview('Sent');
                        }} className="text-[12px] text-[#0EA5E9] hover:text-[#0284C7] font-medium" data-id="element-1192" data-mp="221qtfzboynzy">
                                Issue pass
                              </button>
                            </>}
                          {walletPassStatePreview === 'Sent' && <>
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#DBEAFE] text-[#1E40AF] border border-[#BFDBFE]" data-id="element-1193" data-mp="33jj6g27quy48">
                                Sent
                              </span>
                              <div className="flex items-center gap-1.5" data-id="element-1194" data-mp="lb7x1p0j3fip">
                                <span className="text-[12px] text-[#94A3B8]" data-id="element-1195" data-mp="q9rf3ec0475s">
                                  Sent on {walletPassSentAt}
                                </span>
                                <span className="text-[12px] text-[#94A3B8]" data-id="element-1196" data-mp="z9xs6z3sf54f">
                                  •
                                </span>
                                <button onClick={() => logLoyaltyAudit({
                            eventType: 'Loyalty Status',
                            activity: 'Wallet pass resent'
                          })} className="text-[12px] text-[#0EA5E9] hover:text-[#0284C7] font-medium" data-id="element-1197" data-mp="14893le9qdnor">
                                  Resend
                                </button>
                              </div>
                            </>}
                          {walletPassStatePreview === 'Active' && <>
                              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#D1FAE5] text-[#065F46] border border-[#A7F3D0]" data-id="element-1198" data-mp="1rvqwxe8n8ttu">
                                <CheckCircle className="w-3 h-3" data-id="element-1199" data-mp="19c025goc49fk" />
                                Active
                              </span>
                              <div className="flex items-center gap-1.5" data-id="element-1200" data-mp="1j2cye65ihr1j">
                                <span className="text-[12px] text-[#94A3B8]" data-id="element-1201" data-mp="1j4fkif3jn00z">
                                  Last synced {walletPassLastSyncedAt}
                                </span>
                                <span className="text-[12px] text-[#94A3B8]" data-id="element-1202" data-mp="qf8pvr77mul0">
                                  •
                                </span>
                                <button onClick={() => console.log('View pass')} className="text-[12px] text-[#0EA5E9] hover:text-[#0284C7] font-medium" data-id="element-1203" data-mp="10bbz1h0hbthg">
                                  View pass
                                </button>
                              </div>
                            </>}
                          {walletPassStatePreview === 'SyncIssue' && <>
                              <div className="group relative" data-id="element-1204" data-mp="2o93137vabdwx">
                                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#FEF3C7] text-[#92400E] border border-[#FDE68A] cursor-help" data-id="element-1205" data-mp="2oanrqmehacpi">
                                  Sync issue
                                  <HelpCircle className="w-3 h-3" data-id="element-1206" data-mp="3210zanngjpeb" />
                                </span>
                                <div className="absolute top-full right-0 mt-2 px-3 py-2 bg-[#0F172A] text-white text-xs font-normal normal-case tracking-normal rounded-md shadow-lg w-64 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50" data-id="element-1207" data-mp="370dm58ebwwgw">
                                  Pass hasn't synced for over a week. Customer
                                  may have deleted it or changed devices.
                                  <div className="absolute bottom-full right-4 w-0 h-0 border-l-4 border-r-4 border-b-4 border-l-transparent border-r-transparent border-b-[#0F172A]" data-id="element-1208" data-mp="3prwp5f8csaix" />
                                </div>
                              </div>
                              <button onClick={() => {
                          logLoyaltyAudit({
                            eventType: 'Loyalty Status',
                            activity: 'Wallet pass re-synced',
                            changes: [{
                              field: 'Wallet Pass',
                              before: 'Sync issue',
                              after: 'Active'
                            }]
                          });
                          setWalletPassStatePreview('Active');
                        }} className="text-[12px] text-[#0EA5E9] hover:text-[#0284C7] font-medium" data-id="element-1209" data-mp="3ql4f8p3murd0">
                                Re-sync
                              </button>
                            </>}
                          {walletPassStatePreview === 'Revoked' && <>
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#FEE2E2] text-[#991B1B] border border-[#FECACA]" data-id="element-1210" data-mp="2iuqfpv01eyvo">
                                Revoked
                              </span>
                              <div className="flex items-center gap-1.5" data-id="element-1211" data-mp="2sqa6y43np9e3">
                                <span className="text-[12px] text-[#94A3B8]" data-id="element-1212" data-mp="2xqpkowruoiwl">
                                  Revoked on {walletPassRevokedAt}
                                </span>
                                <span className="text-[12px] text-[#94A3B8]" data-id="element-1213" data-mp="253btzgeqwjkx">
                                  •
                                </span>
                                <button onClick={() => {
                            logLoyaltyAudit({
                              eventType: 'Loyalty Status',
                              activity: 'Wallet pass reissued',
                              changes: [{
                                field: 'Wallet Pass',
                                before: 'Revoked',
                                after: 'Sent'
                              }]
                            });
                            setWalletPassStatePreview('Sent');
                          }} className="text-[12px] text-[#0EA5E9] hover:text-[#0284C7] font-medium" data-id="element-1214" data-mp="2esd974dguwxu">
                                  Reissue
                                </button>
                              </div>
                            </>}
                        </div>
                      </div>
                      <div className="flex justify-between items-center" data-id="element-1215" data-mp="3ff05g5ux85dl">
                        <span className="text-sm text-[#475569]" data-id="element-1216" data-mp="3t7g5cxsiznaa">Status</span>
                        <span className="text-sm font-medium text-[#0F172A]" data-id="element-1217" data-mp="3o8da252ckvfi">
                          {isDormant ? 'Dormant' : isOptedOut ? 'Opted out' : isPaused ? 'Paused' : 'Active'}
                        </span>
                      </div>
                      <div className="flex justify-between items-center" data-id="element-1218" data-mp="22gcrerwoige">
                        <span className="text-sm text-[#475569]" data-id="element-1219" data-mp="lmf93hbvm9kn">
                          Member Since
                        </span>
                        <span className="text-sm font-medium text-[#0F172A]" data-id="element-1220" data-mp="pnh9i1hj4ro7">
                          March 2025
                        </span>
                      </div>
                    </div>
                    {!isMuted && <div className="flex items-center gap-2 mt-6" data-id="element-1221" data-mp="gporgpkfb5x4">
                        <button onClick={openAdjustPoints} className="h-9 px-3 bg-white border border-[#CBD5E1] text-[#0F172A] rounded-md text-sm font-medium hover:bg-[#F8FAFC] transition-colors" data-id="element-1222" data-mp="1xwfmnju8a05">
                          Adjust Points
                        </button>
                        <button onClick={openOverrideTier} className="h-9 px-3 bg-white border border-[#CBD5E1] text-[#0F172A] rounded-md text-sm font-medium hover:bg-[#F8FAFC] transition-colors" data-id="element-1223" data-mp="3u1yy245il5tw">
                          Override Tier
                        </button>
                        <button className="h-9 px-3 text-[#0EA5E9] hover:bg-[#E0F2FE] rounded-md text-sm font-medium transition-colors" data-id="element-1224" data-mp="3k65fa317cg3o">
                          Resend Pass
                        </button>
                        <div className="ml-auto relative" data-id="element-1225" data-mp="3py2rj90fwthr">
                          <button onClick={() => setIsLoyaltyMoreOpen(!isLoyaltyMoreOpen)} className="h-9 w-9 flex items-center justify-center bg-white border border-[#CBD5E1] text-[#0F172A] rounded-md hover:bg-[#F8FAFC] transition-colors" aria-label="More loyalty actions" data-id="element-1226" data-mp="3pwhyjntrxim0">
                            <MoreHorizontal className="w-4 h-4" data-id="element-1227" data-mp="7renlmh4mcdo" />
                          </button>
                          {isLoyaltyMoreOpen && <>
                              <div className="fixed inset-0 z-40" onClick={() => setIsLoyaltyMoreOpen(false)} data-id="element-1228" data-mp="hi9ljo3qj0ey" />
                              <div className="absolute right-0 top-full mt-1 bg-white border border-[#E2E8F0] rounded-md shadow-lg z-50 py-1" style={{
                        width: '180px'
                      }} data-id="element-1229" data-mp="lk4n8tpkvft9">
                                <button onClick={() => {
                          if (enrolmentStatePreview === 'Paused') {
                            logLoyaltyAudit({
                              eventType: 'Loyalty Status',
                              activity: 'Loyalty resumed',
                              changes: [{
                                field: 'Enrolment State',
                                before: 'Paused',
                                after: 'Active'
                              }]
                            });
                            setEnrolmentStatePreview('Active');
                          } else {
                            logLoyaltyAudit({
                              eventType: 'Loyalty Status',
                              activity: 'Loyalty paused by staff',
                              changes: [{
                                field: 'Enrolment State',
                                before: 'Active',
                                after: 'Paused'
                              }]
                            });
                            setEnrolmentStatePreview('Paused');
                          }
                          setIsLoyaltyMoreOpen(false);
                        }} className="w-full text-left px-3 py-2 text-sm text-[#0F172A] hover:bg-[#F8FAFC] transition-colors" data-id="element-1230" data-mp="2b18icaapoot0">
                                  {enrolmentStatePreview === 'Paused' ? 'Resume Loyalty' : 'Pause Loyalty'}
                                </button>
                                <div className="h-px bg-[#E2E8F0] my-1" data-id="element-1231" data-mp="3dum2u3zhc473" />
                                <button onClick={() => {
                          logLoyaltyAudit({
                            eventType: 'Loyalty',
                            activity: 'Points reset - Balance set to 0',
                            changes: [{
                              field: 'Points Balance',
                              before: currentPoints.toLocaleString(),
                              after: '0'
                            }, {
                              field: 'Reason',
                              before: '—',
                              after: 'Manual reset'
                            }]
                          });
                          setIsLoyaltyMoreOpen(false);
                        }} className="w-full text-left px-3 py-2 text-sm text-[#EF4444] hover:bg-[#FEE2E2] transition-colors" data-id="element-1232" data-mp="2zu2yb6u83g9o">
                                  Reset Points
                                </button>
                              </div>
                            </>}
                        </div>
                      </div>}
                  </div>
                </div>

                {/* Tier Progress */}
                <div className={`bg-white border border-[#E2E8F0] rounded-lg shadow-sm overflow-hidden transition-opacity ${isDormant ? 'opacity-60' : ''}`} data-id="element-1233" data-mp="3acydqh5u7m29">
                  <div className="p-6 border-b border-[#E2E8F0]" data-id="element-1234" data-mp="1fxvtaxvnau67">
                    <h3 className="text-base font-semibold text-[#0F172A]" data-id="element-1235" data-mp="1fzqgkiue2ydg">
                      Tier Progress
                    </h3>
                  </div>
                  <div className="p-6" data-id="element-1236" data-mp="1mcuo9xmi8z2c">
                    {isSingleTierProgram ? <div className="mb-6" data-id="element-1237" data-mp="1rd8kmiequkkj">
                        <div className="flex items-center gap-2 text-sm font-medium text-[#0F172A]" data-id="element-1238" data-mp="1rea7rl91axim">
                          <span className="w-2.5 h-2.5 rounded-full bg-[#6F42C1]" data-id="element-1239" data-mp="1wbjugt51gv2r" />
                          <span data-id="element-1240" data-mp="ypcphi4tp0pv">{currentTier}</span>
                        </div>
                        <p className="text-xs text-[#64748B] mt-2" data-id="element-1241" data-mp="13mv2pkpvi23o">
                          This program has a single tier, so there's no status
                          ladder to climb.
                        </p>
                      </div> : <div className="mb-6" data-id="element-1242" data-mp="1ddzi2ljgnoz1">
                        <div className="flex justify-between text-sm font-medium text-[#0F172A] mb-2" data-id="element-1243" data-mp="fxtothy9tyr0">
                          <span data-id="element-1244" data-mp="kwwth1aca2yg">Sommelier</span>
                          <span data-id="element-1245" data-mp="kvlqh3opwz8e">Vintner</span>
                        </div>
                        <div className="h-3 bg-[#E2E8F0] rounded-full overflow-hidden" data-id="element-1246" data-mp="ddnlnchhecbi">
                          <div className={`h-full rounded-full ${isDormant ? 'bg-[#CBD5E1]' : 'bg-[#6F42C1]'}`} style={{
                      width: '85%'
                    }} data-id="element-1247" data-mp="8drgvb0ymguw" />
                        </div>
                        <p className="text-xs text-[#64748B] mt-2" data-id="element-1248" data-mp="3umak5win91a4">
                          {isDormant ? 'Tier progress paused while dormant.' : `${TIER_CALCULATION_BASIS === 'Points' ? '750 more points to reach Vintner' : '$750 more annual spend to reach Vintner'}${isFixedWindow ? ` before ${windowCloseLabel}` : ''}`}
                        </p>
                        <p className="text-xs text-[#94A3B8] mt-1" data-id="element-1249" data-mp="3gs0381cw2bem">
                          {isFixedWindow ? `Sommelier status held until ${windowHeldUntil}.` : 'Tier is reviewed on an ongoing basis.'}
                        </p>
                      </div>}

                    <div data-id="element-1250" data-mp="32ve6wuso8umb">
                      <h4 className="text-sm font-semibold text-[#0F172A] mb-3" data-id="element-1251" data-mp="32v4ru29607ve">
                        Tier History
                      </h4>
                      <div className="space-y-3" data-id="element-1252" data-mp="2x82nw5kyo6a5">
                        {([{
                      tier: 'Sommelier',
                      tierId: 'sommelier',
                      date: 'January 2026',
                      color: '#6F42C1',
                      manuallySetBy: 'Sarah Chen'
                    }, {
                      tier: 'Connoisseur',
                      tierId: 'connoisseur',
                      date: 'September 2025',
                      color: '#3B82F6'
                    }, {
                      tier: 'Enthusiast',
                      tierId: 'enthusiast',
                      date: 'March 2025',
                      color: '#10B981'
                    }] as Array<{
                      tier: string;
                      tierId: string;
                      date: string;
                      color: string;
                      manuallySetBy?: string;
                    }>).map(item => <div key={item.tier} className="flex items-center gap-3" data-id="element-1253" data-mp="cv5pnyuxvg98">
                            <div className="w-2 h-2 rounded-full" style={{
                        backgroundColor: item.color
                      }} data-id="element-1254" data-mp="8vf2e54mfi9g" />
                            <button onClick={() => navigate(`/loyalty/rules?tier=${item.tierId}`)} className="text-sm font-medium text-[#0EA5E9] hover:underline text-left" data-id="element-1255" data-mp="mo4n868eyh4z">
                              {item.tier}
                            </button>
                            {item.manuallySetBy && <Lock className="w-3 h-3 text-[#94A3B8]" data-id="element-1256" data-mp="3m2q7s9npb3v4" />}
                            <span className="text-sm text-[#64748B]" data-id="element-1257" data-mp="3vsodtgyxxzl">
                              {item.date}
                            </span>
                            {item.manuallySetBy && <span className="text-xs text-[#94A3B8] italic" data-id="element-1258" data-mp="1fapfd8wpqx9l">
                                manually set by {item.manuallySetBy}
                              </span>}
                          </div>)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Row 2: Points History */}
              <div className="bg-white border border-[#E2E8F0] rounded-lg shadow-sm overflow-hidden" data-id="element-1259" data-mp="38rbyvv8vmzgx">
                <div className="p-6 border-b border-[#E2E8F0] flex items-center justify-between" data-id="element-1260" data-mp="3dqet0knbjla4">
                  <h3 className="text-base font-semibold text-[#0F172A]" data-id="element-1261" data-mp="3ilaxwldnm34n">
                    Points History
                  </h3>
                  <div className="flex items-center gap-3" data-id="element-1262" data-mp="3sh5nk0gaoygx">
                    <div className="relative" data-id="element-1263" data-mp="3sjibcu8qi31w">
                      <select className="h-8 pl-2 pr-8 text-xs border border-[#CBD5E1] rounded-md bg-white focus:outline-none focus:border-[#0EA5E9] appearance-none" data-id="element-1264" data-mp="dd3ovtqbi4h">
                        <option data-id="element-1265" data-mp="5abtj26d12wv">All Types</option>
                        <option data-id="element-1266" data-mp="2269o399j3x19">Earned</option>
                        <option data-id="element-1267" data-mp="227cap4ztjdbd">Redeemed</option>
                        <option data-id="element-1268" data-mp="27547odfblpmf">Bonus</option>
                      </select>
                      <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 text-[#94A3B8] pointer-events-none" data-id="element-1269" data-mp="1zl1xs0yg7xno" />
                    </div>
                    <div className="relative" data-id="element-1270" data-mp="1urqolt65z4sl">
                      <select className="h-8 pl-2 pr-8 text-xs border border-[#CBD5E1] rounded-md bg-white focus:outline-none focus:border-[#0EA5E9] appearance-none" data-id="element-1271" data-mp="1ptyxh1dcfmpg">
                        <option data-id="element-1272" data-mp="2ije5rjdmj1ho">All Time</option>
                        <option data-id="element-1273" data-mp="2ii34prbkc7u9">Last 30 Days</option>
                        <option data-id="element-1274" data-mp="2dkbaurrcybnf">Last 90 Days</option>
                      </select>
                      <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 text-[#94A3B8] pointer-events-none" data-id="element-1275" data-mp="2dii3tbcu1pew" />
                    </div>
                    <button className="w-8 h-8 flex items-center justify-center rounded-md border border-[#CBD5E1] hover:bg-[#F1F5F9] transition-colors text-[#64748B]" data-id="element-1276" data-mp="24iv0ul4ggbjq">
                      <Download className="w-4 h-4" data-id="element-1277" data-mp="t3ybfa4czkbu" />
                    </button>
                  </div>
                </div>
                <div className="overflow-x-auto" data-id="element-1278" data-mp="ljx7ipra7ijq">
                  <table className="w-full" data-id="element-1279" data-mp="glvt4i4sw7el">
                    <thead className="bg-[#F8FAFC] border-b border-[#E2E8F0]" data-id="element-1280" data-mp="gmyfccr6hlo6">
                      <tr data-id="element-1281" data-mp="cjsbtcvga8le">
                        <th className="px-6 py-3 text-left text-xs font-semibold text-[#94A3B8] uppercase tracking-wider" data-id="element-1282" data-mp="ciz7sjnvlx6y">
                          Date
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-[#94A3B8] uppercase tracking-wider" data-id="element-1283" data-mp="2n5uuz68w1on">
                          Type
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-[#94A3B8] uppercase tracking-wider" data-id="element-1284" data-mp="3p1757d2bfycn">
                          Source
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-semibold text-[#94A3B8] uppercase tracking-wider" data-id="element-1285" data-mp="3g0r42a91p3ay">
                          Amount
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-semibold text-[#94A3B8] uppercase tracking-wider" data-id="element-1286" data-mp="3b4a66oc1nako">
                          Balance
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#E2E8F0]" data-id="element-1287" data-mp="20j49yxs7xk0u">
                      {[{
                    date: 'Feb 10, 2026',
                    type: 'Earned',
                    source: 'Order #4521',
                    sourceType: 'order',
                    sourceId: '4521',
                    amount: '+150',
                    balance: '4,250',
                    color: '#10B981'
                  }, {
                    date: 'Feb 3, 2026',
                    type: 'Redeemed',
                    source: 'Free Shipping reward',
                    sourceType: 'reward',
                    sourceId: 'free-shipping',
                    amount: '-250',
                    balance: '4,100',
                    color: '#EF4444'
                  }, {
                    date: 'Jan 28, 2026',
                    type: 'Earned',
                    source: 'Cellar Door Visit',
                    sourceType: 'rule',
                    sourceId: 'cellar-door-visit',
                    amount: '+100',
                    balance: '4,350',
                    color: '#10B981'
                  }, {
                    date: 'Jan 22, 2026',
                    type: 'Earned',
                    source: 'Order #4488',
                    sourceType: 'order',
                    sourceId: '4488',
                    amount: '+320',
                    balance: '4,250',
                    color: '#10B981'
                  }, {
                    date: 'Jan 15, 2026',
                    type: 'Bonus',
                    source: 'Birthday',
                    sourceType: 'rule',
                    sourceId: 'birthday-bonus',
                    amount: '+200',
                    balance: '3,930',
                    color: '#F59E0B'
                  }, {
                    date: 'Jan 8, 2026',
                    type: 'Earned',
                    source: 'Order #4456',
                    sourceType: 'order',
                    sourceId: '4456',
                    amount: '+280',
                    balance: '3,730',
                    color: '#10B981'
                  }].map((row, i) => <tr key={i} className="hover:bg-[#F8FAFC]" data-id="element-1288" data-mp="2wfh54b7d8ah6" data-mpi={i}>
                          <td className="px-6 py-4 text-sm text-[#0F172A]" data-id="element-1289" data-mp="3k5ulv5ki2mwg">
                            {row.date}
                          </td>
                          <td className="px-6 py-4" data-id="element-1290" data-mp="3p5z7tb46g9u2">
                            <div className="flex items-center gap-2" data-id="element-1291" data-mp="3adypbyy2b4uc">
                              <div className="w-2 h-2 rounded-full" style={{
                          backgroundColor: row.color
                        }} data-id="element-1292" data-mp="3fdtfg0a0jqec" />
                              <span className="text-sm text-[#0F172A]" data-id="element-1293" data-mp="6wympdmxjmdz">
                                {row.type}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4" data-id="element-1294" data-mp="2k3pt8j28zoli">
                            <button onClick={() => {
                        if (row.sourceType === 'order') {
                          navigate('/orders');
                        } else if (row.sourceType === 'reward') {
                          navigate('/loyalty/rewards');
                        } else if (row.sourceType === 'rule') {
                          navigate('/loyalty/rules');
                        }
                      }} className="text-sm font-medium text-[#0EA5E9] hover:underline text-left" data-id="element-1295" data-mp="2f5xzrklyfuf7">
                              {row.source}
                            </button>
                          </td>
                          <td className={`px-6 py-4 text-sm font-medium text-right ${row.amount.startsWith('+') ? 'text-[#10B981]' : 'text-[#EF4444]'}`} data-id="element-1296" data-mp="2o5l52hht1kdj">
                            {row.amount}
                          </td>
                          <td className="px-6 py-4 text-sm text-[#64748B] text-right" data-id="element-1297" data-mp="3p4z7g3lbrta0">
                            {row.balance}
                          </td>
                        </tr>)}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Row 3: Rewards & cellar door visit points */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-1298" data-mp="705lbmhzko5s">
                {/* Available Rewards */}
                <div className="bg-white border border-[#E2E8F0] rounded-lg shadow-sm overflow-hidden" data-id="element-1299" data-mp="bxxmz88fnon6">
                  <div className="p-6 border-b border-[#E2E8F0]" data-id="element-1300" data-mp="fy7wguvfox6s">
                    <h3 className="text-base font-semibold text-[#0F172A]" data-id="element-1301" data-mp="krsj5qrku4qo">
                      Available Rewards
                    </h3>
                  </div>
                  <div className="divide-y divide-[#E2E8F0]" data-id="element-1302" data-mp="uoxbk8c0mwfx">
                    {[{
                  name: 'Free Shipping',
                  rewardId: 'free-shipping',
                  points: '250 pts',
                  available: true
                }, {
                  name: '$10 Off',
                  rewardId: 'ten-dollars-off',
                  points: '1,000 pts',
                  available: true
                }, {
                  name: 'Free Tasting',
                  rewardId: 'free-tasting',
                  points: '1,500 pts',
                  available: true
                }, {
                  name: 'Free Rosé Bottle',
                  rewardId: 'free-rose-bottle',
                  points: '2,500 pts',
                  available: false,
                  needed: '750 pts away'
                }].map(reward => <div key={reward.name} className="px-6 py-4 flex items-center gap-3" data-id="element-1303" data-mp="12nid3lx2b4l5">
                        {reward.available ? <CheckCircle className="w-5 h-5 text-[#10B981]" data-id="element-1304" data-mp="xq05byus6veq" /> : <Circle className="w-5 h-5 text-[#CBD5E1]" data-id="element-1305" data-mp="34fn2oqr39psp" />}
                        <button onClick={() => navigate('/loyalty/rewards')} className="text-sm font-medium text-[#0EA5E9] hover:underline text-left" data-id="element-1306" data-mp="34i0zb41f3a2d">
                          {reward.name}
                        </button>
                        <span className="text-sm text-[#64748B]" data-id="element-1307" data-mp="1jxmdewtrer1z">
                          {reward.points}
                        </span>
                        {!reward.available && <span className="text-xs text-[#94A3B8] ml-auto" data-id="element-1308" data-mp="158pvm81whzo6">
                            {reward.needed}
                          </span>}
                      </div>)}
                  </div>
                </div>

                {/* Issued Rewards */}
                <div className="bg-white border border-[#E2E8F0] rounded-lg shadow-sm overflow-hidden lg:col-span-2" data-id="element-1309" data-mp="10a54cy4mmqbv">
                  <div className="p-6 border-b border-[#E2E8F0]" data-id="element-1310" data-mp="mbf6hg9zgmmg">
                    <h3 className="text-base font-semibold text-[#0F172A]" data-id="element-1311" data-mp="r8ovwvxdgyaa">
                      Issued Rewards
                    </h3>
                    <p className="text-xs text-[#64748B] mt-1" data-id="element-1312" data-mp="2goo8kn0795uo">
                      Every reward this member has claimed or been given, with
                      its code, so you can help if they lose it.
                    </p>
                    <div className="inline-flex rounded-md border border-[#CBD5E1] bg-white p-1 mt-4" role="group" aria-label="Filter issued rewards" data-id="element-1313" data-mp="18i7nbap8hxmx">
                      {(['All', 'Active', 'Used', 'Expired'] as const).map(option => <button key={option} type="button" onClick={() => setIssuedFilter(option)} aria-pressed={issuedFilter === option} className={`px-3 h-8 rounded text-[13px] font-medium transition-colors ${issuedFilter === option ? 'bg-[#E0F2FE] text-[#0EA5E9]' : 'text-[#64748B] hover:text-[#0F172A]'}`} data-id="element-1314" data-mp="up8879td65fw">
                            {option}
                          </button>)}
                    </div>
                  </div>

                  {visibleIssuedRewards.length === 0 ? <div className="px-6 py-10 text-center text-sm text-[#64748B]" data-id="element-1315" data-mp="bs73lq3q27ch">
                      No rewards issued yet.
                    </div> : <div className="overflow-x-auto" data-id="element-1316" data-mp="263ltq80na8sr">
                      <table className="w-full" data-id="element-1317" data-mp="264wtzxuvsyf4">
                        <thead className="bg-[#F8FAFC] border-b border-[#E2E8F0]" data-id="element-1318" data-mp="2do63dr3e0x46">
                          <tr data-id="element-1319" data-mp="2imhfy9l2hlq3">
                            <th className="px-6 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-1320" data-mp="2ilplx374bw1x">
                              Reward
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-1321" data-mp="2sbiwc0viy54l">
                              Code
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-1322" data-mp="1zoy46y785ozz">
                              Issued
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-1323" data-mp="28othp9o95w36">
                              Expires
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-1324" data-mp="3k2yqqh3bow5d">
                              Status
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-[#E2E8F0]" data-id="element-1325" data-mp="k12ujj0c0iba">
                          {visibleIssuedRewards.map(reward => {
                      const TypeIcon = issuedRewardIcon[reward.typeLabel];
                      return <tr key={reward.id} data-id="element-1326" data-mp="3sfbhaag6aevj">
                                <td className="px-6 py-4" data-id="element-1327" data-mp="3sdgvyv0zokqx">
                                  <div className="flex items-start gap-3" data-id="element-1328" data-mp="19a3hu4tp767">
                                    <div className="w-8 h-8 rounded-lg bg-[#F1F5F9] flex items-center justify-center flex-shrink-0" data-id="element-1329" data-mp="3igvpe59bn1kz">
                                      <TypeIcon className="w-4 h-4 text-[#475569]" data-id="element-1330" data-mp="3nf5nanmfkgaa" />
                                    </div>
                                    <div className="min-w-0" data-id="element-1331" data-mp="1gq0s18zguuc5">
                                      <p className="text-sm font-medium text-[#0F172A] m-0" data-id="element-1332" data-mp="1lp3war7mgwj2">
                                        {reward.name}
                                      </p>
                                      <p className="text-xs text-[#64748B] mt-0.5 m-0" data-id="element-1333" data-mp="317tgwahv0g8k">
                                        {reward.typeLabel}
                                      </p>
                                    </div>
                                  </div>
                                </td>
                                <td className="px-6 py-4" data-id="element-1334" data-mp="3owl1afp7juas">
                                  {reward.code ? <div className="flex items-center gap-2" data-id="element-1335" data-mp="3oyysuekoiodl">
                                      <span className="font-mono text-[13px] text-[#0F172A]" data-id="element-1336" data-mp="3kv95ttjnkeuo">
                                        {reward.code}
                                      </span>
                                      <button type="button" onClick={() => copyRewardCode(reward.code as string)} aria-label={`Copy code ${reward.code}`} className="text-[#94A3B8] hover:text-[#0F172A] transition-colors" data-id="element-1337" data-mp="3tx0cp1pxirq4">
                                        {copiedCode === reward.code ? <Check className="w-4 h-4 text-[#10B981]" data-id="element-1338" data-mp="3fkw1cibbf9s" /> : <Copy className="w-4 h-4" data-id="element-1339" data-mp="3gw25iz8cz42" />}
                                      </button>
                                    </div> : <span className="text-sm text-[#94A3B8]" data-id="element-1340" data-mp="8fz6103h4zag">
                                      Applied automatically
                                    </span>}
                                </td>
                                <td className="px-6 py-4 text-sm text-[#475569]" data-id="element-1341" data-mp="me5l6z0n8aks">
                                  {reward.issued}
                                </td>
                                <td className="px-6 py-4 text-sm text-[#475569]" data-id="element-1342" data-mp="raw1nngd186q">
                                  {reward.expires ?? 'No expiry'}
                                </td>
                                <td className="px-6 py-4" data-id="element-1343" data-mp="104shylmufqe8">
                                  <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${issuedRewardStatusStyle[reward.status]}`} data-id="element-1344" data-mp="1556izjq1202r">
                                    {reward.status}
                                  </span>
                                  {reward.status === 'Used' && reward.usedOnOrder && <button onClick={() => navigate('/orders')} className="block text-xs text-[#0EA5E9] hover:underline mt-1" data-id="element-1345" data-mp="1f7agvzxr16ti">
                                        Order {reward.usedOnOrder}
                                      </button>}
                                </td>
                              </tr>;
                    })}
                        </tbody>
                      </table>
                    </div>}
                </div>

                {/* Points from cellar door visits; the visit list itself is on Overview */}
                <div className="bg-white border border-[#E2E8F0] rounded-lg shadow-sm overflow-hidden" data-id="element-1346" data-mp="1t5qodt3fr1cp">
                  <div className="p-6 border-b border-[#E2E8F0]" data-id="element-1347" data-mp="1p4ow5w7xrpk8">
                    <div className="flex items-center justify-between" data-id="element-1348" data-mp="2lpyxefa8r12o">
                      <h3 className="text-base font-semibold text-[#0F172A]" data-id="element-1349" data-mp="2gxfovwqeuw4q">
                        Points from cellar door visits
                      </h3>
                      {onViewVisits && <button type="button" onClick={onViewVisits} className="inline-flex items-center gap-1 text-[13px] text-[#0EA5E9] hover:text-[#0284C7] font-medium transition-colors" data-id="element-1350" data-mp="26ehjbn48n6gt">
                          View visits
                          <ArrowRight className="w-3.5 h-3.5" data-id="element-1351" data-mp="1whmmd8nm5e4n" />
                        </button>}
                    </div>
                    <p className="text-xs text-[#64748B] mt-1" data-id="element-1352" data-mp="2k6p7wrldbr4f">
                      Visit history is on the Overview tab.
                    </p>
                  </div>
                  {visits.length === 0 ? <div className="px-6 py-10 text-center text-sm text-[#64748B]" data-id="element-1353" data-mp="2bdc7z5ovzp8u">
                      No cellar door visits yet
                    </div> : <div className="divide-y divide-[#E2E8F0]" data-id="element-1354" data-mp="3351mwtyynv7x">
                      {visits.slice(0, 3).map(visit => <div key={visit.id} className="px-6 py-4 flex justify-between items-center" data-id="element-1355" data-mp="37vqd85ywmm5h">
                          <span className="text-sm text-[#475569]" data-id="element-1356" data-mp="wgrg76650je6">
                            {formatShortDate(visit.date)} · {visit.location}
                          </span>
                          {visit.pointsEarned != null && <span className="text-sm font-medium text-[#10B981]" data-id="element-1357" data-mp="166b0mg9i0lb5">
                              +{visit.pointsEarned} pts
                            </span>}
                        </div>)}
                    </div>}
                </div>
              </div>
            </div>
          </>}
      </div>

      {/* Unlock Tier Confirmation Modal */}
      {isUnlockTierModalOpen && <>
          <div className="fixed inset-0 bg-black/50 z-50" onClick={() => setIsUnlockTierModalOpen(false)} data-id="element-1358" data-mp="ng32ydokin2c" />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-1359" data-mp="ih9mqdhwchyh">
            <div className="bg-white rounded-lg shadow-xl w-[420px] overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-1360" data-mp="1yv87ln89evol">
              <div className="px-6 py-4 border-b border-[#E2E8F0]" data-id="element-1361" data-mp="1twol9lpr7hvp">
                <h2 className="text-lg font-semibold text-[#0F172A]" data-id="element-1362" data-mp="1tyhpyvgt3s12">
                  Unlock tier?
                </h2>
              </div>
              <div className="px-6 py-5 space-y-3" data-id="element-1363" data-mp="25xn4xv3g6wkx">
                <p className="text-sm text-[#475569]" data-id="element-1364" data-mp="2609chuqc9c35">
                  This will unlock the tier and auto-calculate based on annual
                  spend.{' '}
                  <span className="font-semibold text-[#0F172A]" data-id="element-1365" data-mp="2fvb3kw46co8d">
                    {contactName}
                  </span>{' '}
                  may move to a different tier immediately.
                </p>
                <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-md p-3 text-xs text-[#475569]" data-id="element-1366" data-mp="1n8gfzqc298qo">
                  <div className="flex items-center gap-1.5 mb-1" data-id="element-1367" data-mp="1s5qdlt9lsqpl">
                    <Lock className="w-3 h-3 text-[#64748B]" data-id="element-1368" data-mp="1wytaaa9ogga0" />
                    <span className="font-medium text-[#0F172A]" data-id="element-1369" data-mp="zim6niqya2b6">
                      Currently locked
                    </span>
                  </div>
                  <div data-id="element-1370" data-mp="14fw5fp08bqbo">
                    Set by {tierLockedBy} on {tierLockedAt}
                  </div>
                </div>
              </div>
              <div className="px-6 py-4 border-t border-[#E2E8F0] flex items-center justify-end gap-3" data-id="element-1371" data-mp="3dqerqbvhewz9">
                <button onClick={() => setIsUnlockTierModalOpen(false)} className="h-9 px-4 text-sm font-medium text-[#475569] hover:text-[#0F172A] transition-colors" data-id="element-1372" data-mp="38qiwb10uodvb">
                  Cancel
                </button>
                <button onClick={() => {
              logLoyaltyAudit({
                eventType: 'Tier',
                activity: 'Tier unlocked - automatic recalculation re-enabled',
                changes: [{
                  field: 'Tier Lock',
                  before: 'Locked',
                  after: 'Unlocked'
                }]
              });
              setIsUnlockTierModalOpen(false);
            }} className="h-9 px-4 bg-[#0EA5E9] text-white text-sm font-medium rounded-md hover:bg-[#0284C7] transition-colors" data-id="element-1373" data-mp="2ytwd0go69uve">
                  Unlock tier
                </button>
              </div>
            </div>
          </div>
        </>}

      {/* Re-enrol Confirmation Modal */}
      {isReEnrolModalOpen && <>
          <div className="fixed inset-0 bg-black/50 z-50" onClick={() => setIsReEnrolModalOpen(false)} data-id="element-1374" data-mp="7svpea30g84x" />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-1375" data-mp="3q1e4wkbph50f">
            <div className="bg-white rounded-lg shadow-xl w-[420px] overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-1376" data-mp="3uwtgf5m8qoto">
              <div className="px-6 py-4 border-b border-[#E2E8F0]" data-id="element-1377" data-mp="3g7nj5n6yr8of">
                <h2 className="text-lg font-semibold text-[#0F172A]" data-id="element-1378" data-mp="3l1rra9rytmr3">
                  Re-enrol member?
                </h2>
              </div>
              <div className="px-6 py-5" data-id="element-1379" data-mp="3c7wnb52ky2d1">
                <p className="text-sm text-[#475569]" data-id="element-1380" data-mp="3c8ohqckfxu1q">
                  <span className="font-semibold text-[#0F172A]" data-id="element-1381" data-mp="2xama9raagof7">
                    {contactName}
                  </span>{' '}
                  will rejoin the loyalty program and start earning again. Their
                  previous points and tier history will be restored.
                </p>
              </div>
              <div className="px-6 py-4 border-t border-[#E2E8F0] flex items-center justify-end gap-3" data-id="element-1382" data-mp="2emtwv8hjupa8">
                <button onClick={() => setIsReEnrolModalOpen(false)} className="h-9 px-4 text-sm font-medium text-[#475569] hover:text-[#0F172A] transition-colors" data-id="element-1383" data-mp="2smd664m3k6o4">
                  Cancel
                </button>
                <button onClick={() => {
              logLoyaltyAudit({
                eventType: 'Loyalty Status',
                activity: 'Member re-enrolled by staff',
                changes: [{
                  field: 'Enrolment State',
                  before: 'Opted out',
                  after: 'Active'
                }, {
                  field: 'Reason',
                  before: '—',
                  after: 'Member requested'
                }]
              });
              setEnrolmentStatePreview('Active');
              setIsReEnrolModalOpen(false);
            }} className="h-9 px-4 bg-[#0EA5E9] text-white text-sm font-medium rounded-md hover:bg-[#0284C7] transition-colors" data-id="element-1384" data-mp="31lq90efptwzv">
                  Re-enrol
                </button>
              </div>
            </div>
          </div>
        </>}

      {/* Single-member Adjust Points Modal (4.1) */}
      {isAdjustPointsModalOpen && <>
          <div className="fixed inset-0 bg-black/50 z-50" onClick={() => setIsAdjustPointsModalOpen(false)} data-id="element-1385" data-mp="1omgkqminsx0" />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-1386" data-mp="3a466bfnzat7b">
            <div className="bg-white rounded-lg shadow-xl w-[440px] max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-id="element-1387" data-mp="356ee0kjf952l">
              <div className="px-6 py-4 border-b border-[#E2E8F0]" data-id="element-1388" data-mp="3e5rlj7nzjjko">
                <h2 className="text-lg font-semibold text-[#0F172A]" data-id="element-1389" data-mp="3e3exqdvjqezp">
                  Adjust points for {contactName}
                </h2>
                <p className="text-[13px] text-[#64748B] mt-0.5" data-id="element-1390" data-mp="2mevbeetb7t13">
                  Current balance {currentPoints.toLocaleString()} points ·{' '}
                  {currentTier}
                </p>
              </div>
              <div className="px-6 py-5 space-y-5 overflow-y-auto" data-id="element-1391" data-mp="2h26m3vyzkhnf">
                <div data-id="element-1392" data-mp="22ch993hj6p7b">
                  <label className="block text-xs font-medium text-[#475569] mb-2" data-id="element-1393" data-mp="22da6ndulo2cs">
                    Adjustment Type
                  </label>
                  <div className="flex gap-4 flex-wrap" data-id="element-1394" data-mp="2797pbqtc5ksq">
                    {([{
                  key: 'add',
                  label: 'Add Points'
                }, {
                  key: 'remove',
                  label: 'Remove Points'
                }, {
                  key: 'reset',
                  label: 'Reset to Zero'
                }] as const).map(opt => {
                  const active = adjustmentType === opt.key;
                  const danger = opt.key === 'reset';
                  return <label key={opt.key} className="flex items-center gap-2 cursor-pointer" data-id="element-1395" data-mp="19pwhw3122d3r">
                          <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${active ? danger ? 'border-[#EF4444]' : 'border-[#0EA5E9]' : 'border-[#CBD5E1]'}`} onClick={() => {
                      setAdjustmentType(opt.key);
                      setAdjustReasonPreset('');
                      setAdjustReasonCustom('');
                      setIsAdjustReasonOpen(false);
                      setResetConfirmation('');
                    }} data-id="element-1396" data-mp="1ekjzdhxn678u">
                            {active && <div className={`w-2 h-2 rounded-full ${danger ? 'bg-[#EF4444]' : 'bg-[#0EA5E9]'}`} data-id="element-1397" data-mp="2xmngx6owtmp5" />}
                          </div>
                          <span className="text-sm text-[#0F172A]" data-id="element-1398" data-mp="2ok4j50zze09r">
                            {opt.label}
                          </span>
                        </label>;
                })}
                  </div>
                </div>
                {adjustmentType === 'reset' && <div className="space-y-4" data-id="element-1399" data-mp="mqx5dw00nqpz">
                    <div className="bg-[#FEE2E2] border border-[#FECACA] rounded-[6px] px-4 py-3 flex items-start gap-2.5" data-id="element-1400" data-mp="htwv18w4w1t2">
                      <AlertTriangle className="w-4 h-4 text-[#EF4444] mt-0.5 shrink-0" data-id="element-1401" data-mp="pd6759w0yelt" />
                      <p className="text-[13px] text-[#991B1B]" data-id="element-1402" data-mp="ubhfthal2j2g">
                        This will set {contactName}'s current points balance to
                        0. Lifetime points will not be affected. This action
                        cannot be undone.
                      </p>
                    </div>
                    <div data-id="element-1403" data-mp="6go8s9mo7ggl">
                      <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-1404" data-mp="bdy60bgawwez">
                        Confirm Reset
                      </label>
                      <input type="text" value={resetConfirmation} onChange={e => setResetConfirmation(e.target.value)} placeholder="Type RESET to confirm" className="w-full h-[40px] px-3 bg-white border border-[#CBD5E1] rounded-[6px] text-[14px] text-[#0F172A] placeholder:text-[#94A3B8] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20 transition-all duration-200" data-id="element-1405" data-mp="gbg6ddg963bd" />
                    </div>
                  </div>}
                {adjustmentType !== 'reset' && <Input type="number" label="Amount" value={adjustmentAmount} onChange={e => setAdjustmentAmount(e.target.value)} placeholder="0" data-id="element-1406" data-mp="1qc2e21kpidbf" />}
                <div data-id="element-1407" data-mp="248fm6k3cp2d8">
                  <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-1408" data-mp="1zbz0dwjmj03b">
                    Reason <span className="text-[#EF4444]" data-id="element-1409" data-mp="2db8emqo9rk4n">*</span>
                  </label>
                  <div className="relative" data-id="element-1410" data-mp="299v4w5i22wsg">
                    <button type="button" onClick={() => setIsAdjustReasonOpen(!isAdjustReasonOpen)} className="w-full h-[40px] px-3 bg-white border border-[#CBD5E1] rounded-[6px] text-[14px] text-left flex items-center justify-between focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20 transition-all duration-200" data-id="element-1411" data-mp="2993lru5narj2">
                      <span className={adjustReasonPreset ? 'text-[#0F172A]' : 'text-[#94A3B8]'} data-id="element-1412" data-mp="2s0cx88yz7qda">
                        {adjustReasonPreset || 'Select a reason...'}
                      </span>
                      <ChevronDown className={`w-4 h-4 text-[#475569] transition-transform ${isAdjustReasonOpen ? 'rotate-180' : ''}`} data-id="element-1413" data-mp="3bkbohx43arao" />
                    </button>
                    {isAdjustReasonOpen && <div className="absolute z-10 w-full mt-1 bg-white border border-[#E2E8F0] rounded-[6px] shadow-lg overflow-hidden" data-id="element-1414" data-mp="31q34jxt4k6o4">
                        {(adjustmentType === 'add' ? CD_ADD_REASONS : CD_REMOVE_REASONS).map(reason => <button key={reason} type="button" onClick={() => {
                    setAdjustReasonPreset(reason);
                    setIsAdjustReasonOpen(false);
                    if (reason !== 'Other') setAdjustReasonCustom('');
                  }} className={`w-full px-3 py-2.5 text-[14px] text-left hover:bg-[#F1F5F9] transition-colors ${adjustReasonPreset === reason ? 'bg-[#F0F9FF] text-[#0EA5E9] font-medium' : 'text-[#0F172A]'}`} data-id="element-1415" data-mp="2wovwdxn2l5gx">
                            {reason}
                          </button>)}
                      </div>}
                  </div>
                  {adjustReasonPreset === 'Other' && <input type="text" value={adjustReasonCustom} onChange={e => setAdjustReasonCustom(e.target.value)} placeholder="Enter custom reason..." className="w-full h-[40px] px-3 mt-2 bg-white border border-[#CBD5E1] rounded-[6px] text-[14px] text-[#0F172A] placeholder:text-[#94A3B8] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20 transition-all duration-200" data-id="element-1416" data-mp="1q2j40qn10njn" />}
                </div>
                {adjustmentType !== 'reset' && adjustAmountNum > 0 && <div className="space-y-3" data-id="element-1417" data-mp="23uhaetpcs5c4">
                    <div className="bg-[#F1F5F9] rounded-[6px] px-4 py-3" data-id="element-1418" data-mp="23t62chovkbf7">
                      <p className="text-[13px] text-[#0F172A]" data-id="element-1419" data-mp="1yw5wa6s0z8o3">
                        New balance:{' '}
                        <span className="font-semibold" data-id="element-1420" data-mp="sgde3gvam53w">
                          {adjustNewBalance.toLocaleString()}
                        </span>{' '}
                        points
                      </p>
                    </div>
                    {adjustGoesBelowZero && <div className="bg-[#FEF3C7] border border-[#FDE68A] rounded-[6px] px-4 py-3 flex items-start gap-2.5" data-id="element-1421" data-mp="342notod284bz">
                        <AlertTriangle className="w-4 h-4 text-[#D97706] mt-0.5 shrink-0" data-id="element-1422" data-mp="391h1jekivrb3" />
                        <p className="text-[13px] text-[#92400E]" data-id="element-1423" data-mp="3dsz4u9a0q3z8">
                          Removing more than the current balance. Points will be
                          set to 0 (cannot go negative).
                        </p>
                      </div>}
                    {adjustNewTier !== currentTier && <div className="bg-[#DBEAFE] border border-[#BFDBFE] rounded-[6px] px-4 py-3 flex items-start gap-2.5" data-id="element-1424" data-mp="3rsi1o5xdpsw8">
                        <Info className="w-4 h-4 text-[#2563EB] mt-0.5 shrink-0" data-id="element-1425" data-mp="kw1in9vc8k4" />
                        <p className="text-[13px] text-[#1E40AF] flex items-center gap-1.5 flex-wrap" data-id="element-1426" data-mp="1xf0jyzjnnq94">
                          Tier will move
                          <span className="font-medium" data-id="element-1427" data-mp="22dk7h4dwda3l">{currentTier}</span>
                          <ArrowRight className="w-3 h-3 shrink-0" data-id="element-1428" data-mp="8459cc2q7z72" />
                          <span className="font-semibold" data-id="element-1429" data-mp="36wscetbe17e">{adjustNewTier}</span>
                          {tierLocked && <span className="text-[#1E40AF]" data-id="element-1430" data-mp="hvsw3zoc8s27">
                              — tier is locked, so it won't change automatically
                            </span>}
                        </p>
                      </div>}
                  </div>}
              </div>
              <div className="px-6 py-4 border-t border-[#E2E8F0] flex items-center justify-end gap-3" data-id="element-1431" data-mp="3ldt7lot3rfkk">
                <button onClick={() => setIsAdjustPointsModalOpen(false)} className="h-9 px-4 text-sm font-medium text-[#475569] hover:text-[#0F172A] transition-colors" data-id="element-1432" data-mp="3gf9hrdbe1jmx">
                  Cancel
                </button>
                {adjustmentType === 'reset' ? <button disabled={adjustApplyDisabled} onClick={() => {
              logLoyaltyAudit({
                eventType: 'Loyalty',
                activity: 'Points reset - Balance set to 0',
                changes: [{
                  field: 'Points Balance',
                  before: currentPoints.toLocaleString(),
                  after: '0'
                }, {
                  field: 'Reason',
                  before: '—',
                  after: adjustReasonResolved
                }]
              });
              setIsAdjustPointsModalOpen(false);
            }} className={`h-9 px-4 text-sm font-medium rounded-md transition-colors ${adjustApplyDisabled ? 'bg-[#FCA5A5] text-white cursor-not-allowed' : 'bg-[#EF4444] text-white hover:bg-[#DC2626]'}`} data-id="element-1433" data-mp="1aivoea2f86k0">
                    Reset Points
                  </button> : <button disabled={adjustApplyDisabled} onClick={() => {
              logLoyaltyAudit({
                eventType: 'Loyalty',
                activity: `Points adjusted - ${adjustmentType === 'add' ? '+' : '-'}${adjustAmountNum} points (${adjustReasonResolved})`,
                changes: [{
                  field: 'Points Balance',
                  before: currentPoints.toLocaleString(),
                  after: adjustNewBalance.toLocaleString()
                }, {
                  field: 'Reason',
                  before: '—',
                  after: adjustReasonResolved
                }]
              });
              setIsAdjustPointsModalOpen(false);
            }} className={`h-9 px-4 text-sm font-medium rounded-md transition-colors ${adjustApplyDisabled ? 'bg-[#BAE6FD] text-white cursor-not-allowed' : 'bg-[#0EA5E9] text-white hover:bg-[#0284C7]'}`} data-id="element-1434" data-mp="1n0gbwq8991x3">
                    Apply Adjustment
                  </button>}
              </div>
            </div>
          </div>
        </>}

      {/* Single-member Override Tier Modal (4.1) */}
      {isOverrideTierModalOpen && <>
          <div className="fixed inset-0 bg-black/50 z-50" onClick={() => setIsOverrideTierModalOpen(false)} data-id="element-1435" data-mp="3fzzu4vl61mwf" />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-1436" data-mp="vc507wvqht7t">
            <div className="bg-white rounded-lg shadow-xl w-[440px] max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-id="element-1437" data-mp="vdy8thpwqnig">
              <div className="px-6 py-4 border-b border-[#E2E8F0]" data-id="element-1438" data-mp="2aw48rcj414q8">
                <h2 className="text-lg font-semibold text-[#0F172A]" data-id="element-1439" data-mp="26430s2sz6060">
                  Override tier for {contactName}
                </h2>
                <p className="text-[13px] text-[#64748B] mt-0.5" data-id="element-1440" data-mp="214i3rxxd81vx">
                  Current tier {currentTier}
                </p>
              </div>
              <div className="px-6 py-5 space-y-5 overflow-y-auto" data-id="element-1441" data-mp="1s4l9ngs7iath">
                <div data-id="element-1442" data-mp="1s3a5hfuun11u">
                  <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-1443" data-mp="1s6xvw64zz7cc">
                    Target Tier <span className="text-[#EF4444]" data-id="element-1444" data-mp="1i6f850abb6p5">*</span>
                  </label>
                  <div className="relative" data-id="element-1445" data-mp="1i8ryo1u4ypdt">
                    <button type="button" onClick={() => setIsTargetTierOpen(!isTargetTierOpen)} className="w-full h-[40px] px-3 bg-white border border-[#CBD5E1] rounded-[6px] text-[14px] text-left flex items-center justify-between focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20 transition-all duration-200" data-id="element-1446" data-mp="3hjrq30tpbb9z">
                      <span className={targetTier ? 'text-[#0F172A]' : 'text-[#94A3B8]'} data-id="element-1447" data-mp="1fo2dygrlygkt">
                        {targetTier || 'Select a tier...'}
                      </span>
                      <ChevronDown className={`w-4 h-4 text-[#475569] transition-transform ${isTargetTierOpen ? 'rotate-180' : ''}`} data-id="element-1448" data-mp="15wpizgaijaak" />
                    </button>
                    {isTargetTierOpen && <div className="absolute z-10 w-full mt-1 bg-white border border-[#E2E8F0] rounded-[6px] shadow-lg overflow-hidden" data-id="element-1449" data-mp="1yjsctv48uwuh">
                        {CD_TIERS.map(tier => <button key={tier.name} type="button" onClick={() => {
                    setTargetTier(tier.name);
                    setIsTargetTierOpen(false);
                    if (cdGetTierRank(tier.name) < cdGetTierRank(currentTier)) {
                      setLockTierOnOverride(true);
                    }
                  }} className={`w-full px-3 py-2.5 text-[14px] text-left hover:bg-[#F1F5F9] transition-colors ${targetTier === tier.name ? 'bg-[#F0F9FF] text-[#0EA5E9] font-medium' : 'text-[#0F172A]'}`} data-id="element-1450" data-mp="1omdzw5hj4q6x">
                            {tier.name}
                          </button>)}
                      </div>}
                  </div>
                </div>
                <div data-id="element-1451" data-mp="al8576m7usyg">
                  <label className="flex items-start gap-3 cursor-pointer" data-id="element-1452" data-mp="3j2mg9b2v3ndd">
                    <div onClick={() => setLockTierOnOverride(!lockTierOnOverride)} className={`w-[18px] h-[18px] mt-0.5 rounded border flex items-center justify-center shrink-0 transition-colors ${lockTierOnOverride ? 'bg-[#0EA5E9] border-[#0EA5E9]' : 'border-[#CBD5E1] bg-white'}`} data-id="element-1453" data-mp="3j0jgw0ftkhvz">
                      {lockTierOnOverride && <svg width="12" height="12" viewBox="0 0 12 12" fill="none" data-id="element-1454" data-mp="30cejdapr9t5m">
                          <path d="M2.5 6L5 8.5L9.5 3.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-1455" data-mp="30a1ra4qaa2eu" />
                        </svg>}
                    </div>
                    <div data-id="element-1456" data-mp="1yjtrf35j3mxf">
                      <div className="flex items-center gap-2" data-id="element-1457" data-mp="22ni23upgiymw">
                        <span className="text-[14px] font-medium text-[#0F172A]" data-id="element-1458" data-mp="22nrprbn14tpf">
                          Lock tier — prevent automatic recalculation
                        </span>
                        {overrideIsDowngrade && <span className="text-[11px] font-medium text-[#0EA5E9] bg-[#F0F9FF] px-1.5 py-0.5 rounded" data-id="element-1459" data-mp="2cfe9jwdfo13s">
                            Recommended
                          </span>}
                      </div>
                      <p className="text-[12px] text-[#64748B] mt-1" data-id="element-1460" data-mp="2qd2v76fbrc9w">
                        {contactName} will stay in this tier regardless of
                        points until manually unlocked
                      </p>
                    </div>
                  </label>
                </div>
                <div data-id="element-1461" data-mp="10y66beeiwr17">
                  <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-1462" data-mp="10xmoryssc4np">
                    Reason <span className="text-[#EF4444]" data-id="element-1463" data-mp="tf5alf9ys1oe">*</span>
                  </label>
                  <div className="relative" data-id="element-1464" data-mp="oo6ry91i6rhz">
                    <button type="button" onClick={() => setIsOverrideReasonOpen(!isOverrideReasonOpen)} className="w-full h-[40px] px-3 bg-white border border-[#CBD5E1] rounded-[6px] text-[14px] text-left flex items-center justify-between focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20 transition-all duration-200" data-id="element-1465" data-mp="on55l8f1eiky">
                      <span className={overrideReasonPreset ? 'text-[#0F172A]' : 'text-[#94A3B8]'} data-id="element-1466" data-mp="17eo698ll9kjt">
                        {overrideReasonPreset || 'Select a reason...'}
                      </span>
                      <ChevronDown className={`w-4 h-4 text-[#475569] transition-transform ${isOverrideReasonOpen ? 'rotate-180' : ''}`} data-id="element-1467" data-mp="ye01z5lzyyk4" />
                    </button>
                    {isOverrideReasonOpen && <div className="absolute z-10 w-full mt-1 bg-white border border-[#E2E8F0] rounded-[6px] shadow-lg overflow-hidden" data-id="element-1468" data-mp="3j3ywode17b4s">
                        {CD_TIER_REASONS.map(reason => <button key={reason} type="button" onClick={() => {
                    setOverrideReasonPreset(reason);
                    setIsOverrideReasonOpen(false);
                    if (reason !== 'Other') setOverrideReasonCustom('');
                  }} className={`w-full px-3 py-2.5 text-[14px] text-left hover:bg-[#F1F5F9] transition-colors ${overrideReasonPreset === reason ? 'bg-[#F0F9FF] text-[#0EA5E9] font-medium' : 'text-[#0F172A]'}`} data-id="element-1469" data-mp="3klw998qy1fgd">
                            {reason}
                          </button>)}
                      </div>}
                  </div>
                  {overrideReasonPreset === 'Other' && <input type="text" value={overrideReasonCustom} onChange={e => setOverrideReasonCustom(e.target.value)} placeholder="Enter custom reason..." className="w-full h-[40px] px-3 mt-2 bg-white border border-[#CBD5E1] rounded-[6px] text-[14px] text-[#0F172A] placeholder:text-[#94A3B8] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20 transition-all duration-200" data-id="element-1470" data-mp="2wvjqf2u5tyb0" />}
                </div>
                {targetTier && <div className="space-y-3" data-id="element-1471" data-mp="1zbacknurs4y9">
                    <div className="bg-[#F1F5F9] rounded-[6px] px-4 py-3" data-id="element-1472" data-mp="2d9qiwdopztfv">
                      <p className="text-[13px] text-[#0F172A] flex items-center gap-1.5 flex-wrap" data-id="element-1473" data-mp="2db1kc6up0p3t">
                        {contactName} will move
                        <span className="font-medium" data-id="element-1474" data-mp="2n74nng1a6tsc">{currentTier}</span>
                        <ArrowRight className="w-3 h-3 shrink-0" data-id="element-1475" data-mp="2i8uqizvzxj43" />
                        <span className="font-semibold" data-id="element-1476" data-mp="2i8l0jcaybbye">{targetTier}</span>
                      </p>
                    </div>
                    {overrideIsDowngrade && <div className="bg-[#FEE2E2] border border-[#FECACA] rounded-[6px] px-4 py-3 flex items-start gap-2.5" data-id="element-1477" data-mp="2ry4kymebbdvd">
                        <AlertTriangle className="w-4 h-4 text-[#EF4444] mt-0.5 shrink-0" data-id="element-1478" data-mp="35z78z4ovay8x" />
                        <p className="text-[13px] text-[#991B1B]" data-id="element-1479" data-mp="310drz2adgp40">
                          This moves {contactName} to a lower tier than they
                          currently hold. Tier lock has been switched on so they
                          won't be auto-evaluated back up.
                        </p>
                      </div>}
                  </div>}
              </div>
              <div className="px-6 py-4 border-t border-[#E2E8F0] flex items-center justify-end gap-3" data-id="element-1480" data-mp="q6outberbe23">
                <button onClick={() => setIsOverrideTierModalOpen(false)} className="h-9 px-4 text-sm font-medium text-[#475569] hover:text-[#0F172A] transition-colors" data-id="element-1481" data-mp="3ookdqc5c9gdv">
                  Cancel
                </button>
                <button disabled={overrideApplyDisabled} onClick={() => {
              logLoyaltyAudit({
                eventType: 'Tier',
                activity: `Tier manually changed - Set to ${targetTier}`,
                changes: [{
                  field: 'Tier',
                  before: currentTier,
                  after: targetTier
                }, {
                  field: 'Reason',
                  before: '—',
                  after: overrideReasonResolved
                }, ...(lockTierOnOverride ? [{
                  field: 'Tier Lock',
                  before: 'Unlocked',
                  after: 'Locked'
                }] : [])]
              });
              setIsOverrideTierModalOpen(false);
            }} className={`h-9 px-4 text-sm font-medium rounded-md transition-colors ${overrideApplyDisabled ? 'bg-[#BAE6FD] text-white cursor-not-allowed' : 'bg-[#0EA5E9] text-white hover:bg-[#0284C7]'}`} data-id="element-1482" data-mp="2fsvzubvvilc">
                  Apply Change
                </button>
              </div>
            </div>
          </div>
        </>}
    </>;
}